/* eslint-disable @typescript-eslint/no-explicit-any */
import { datadogLogs } from "@datadog/browser-logs";
import { RecaptchaGuardMessage, RECAPTCHA_HEADERS } from "@simplyk/common";
import { serializeError } from "serialize-error";

import { isTest } from "../constants/env";
import { RecaptchaAction } from "../enums/recaptcha";

const RetryAllowed = 2;

interface GrecaptchaEnterprise {
  ready(callback: () => void): void;
  execute(siteKey: string, options?: { action?: string }): Promise<string>;
}

export const getGoogleRecaptcha = (): GrecaptchaEnterprise | undefined => {
  // type-coverage:ignore-next-line
  return (window as any).grecaptcha?.enterprise;
};

export const useRecaptchaRetry = (onRecaptchaScriptNotLoaded?: () => void) => {
  const getRecaptchaToken = async (
    action: RecaptchaAction,
    browserErrorEmail: string | undefined | null,
    retryCount = 0
  ): Promise<string | null | undefined> => {
    try {
      if (isTest) {
        return "test";
      }

      const grecaptcha = getGoogleRecaptcha();

      if (!grecaptcha || !grecaptcha.ready) {
        datadogLogs.logger.error("[useRecaptchaRetry] Recaptcha script not loaded", {
          browserErrorEmail: browserErrorEmail || "undefined",
          recaptchaIsPresentInWindow: (!!grecaptcha).toString(),
          recaptchaReadyIsPresentInWindow: (!!grecaptcha?.ready).toString(),
        });

        if (onRecaptchaScriptNotLoaded) {
          onRecaptchaScriptNotLoaded();
        }

        return undefined;
      }

      return await new Promise<string>((resolve, reject) => {
        grecaptcha.ready(() => {
          grecaptcha
            .execute(process.env.NEXT_PUBLIC_RECAPTCHA_KEY!, { action })
            .then((token: string | null) => {
              if (!token) {
                reject(new Error("Not able to fetch recaptcha token"));
              } else {
                resolve(token);
              }
            })
            .catch((error: unknown) => {
              reject(error);
            });
        });
      });
    } catch (error) {
      if (retryCount < RetryAllowed) {
        return getRecaptchaToken(action, browserErrorEmail, retryCount + 1);
      }
      datadogLogs.logger.error("[useRecaptchaRetry] Not able to fetch recaptcha token after retries", {
        browserErrorEmail: browserErrorEmail || "undefined",
        error: serializeError(error),
      });
      return null;
    }
  };

  const executeTrpc = async <T, K>(
    action: RecaptchaAction,
    browserErrorEmail: string | undefined | null,
    request: (args: K) => T & { error?: { code?: string; message?: string } },
    args: K,
    retryCount: number = 0,
    onRecaptchaScriptNotLoaded?: () => void
  ): Promise<T & { error?: { code?: string; message?: string } }> => {
    const recaptchaScriptStartTime = Date.now();
    const recaptchaToken = await getRecaptchaToken(action, browserErrorEmail, retryCount);
    const recaptchaScriptEndTime = Date.now();
    const recaptchaHeaders = {
      [RECAPTCHA_HEADERS.TOKEN]: recaptchaToken || "",
      [RECAPTCHA_HEADERS.MISSING_SCRIPT]: (!getGoogleRecaptcha()?.execute).toString(),
      [RECAPTCHA_HEADERS.SCRIPT_START_TIME]: recaptchaScriptStartTime.toString(),
      [RECAPTCHA_HEADERS.SCRIPT_END_TIME]: recaptchaScriptEndTime.toString(),
      [RECAPTCHA_HEADERS.RETRY_COUNT]: retryCount.toString(),
      [RECAPTCHA_HEADERS.BROWSER_ERROR_EMAIL]: browserErrorEmail || "",
    };

    const res = await request({ ...args, recaptchaHeaders });

    const error = res.error;
    if (!error) {
      return res;
    }

    const message = error.message;
    if (message === RecaptchaGuardMessage && retryCount < RetryAllowed) {
      const bypassVerificationEmail = "browser_error_success@mailinator.com";
      const newBrowserErrorEmail = browserErrorEmail === bypassVerificationEmail ? undefined : browserErrorEmail;
      return executeTrpc(action, newBrowserErrorEmail, request, args, retryCount + 1, onRecaptchaScriptNotLoaded);
    }

    return res;
  };

  return { executeTrpc };
};
